import {
  useAllProducts,
  useCurrentLocation,
  useCurrentWorkspace,
  useCustomMutation,
  useProduct,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocalSearchParams } from 'expo-router';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { RadioButton, Text } from 'react-native-paper';
import { string, object as yupObject } from 'yup';
import { Layout, Spacing } from '../../styles';
import { Colors } from '../../themes';
import { ControlledCheckboxItems, ControlledTextInput, Select } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { LeftButton, SaveButton } from '../navigation';

export type NewOemAssetStyles = {
  container: ViewStyle;
  contentContainer: ViewStyle;
  imageGalleryContainer?: ViewStyle;
};

export type NewOemAssetProps = {
  onCancel: () => void;
  topComponentBar: any;
  styles: StyleSheet.NamedStyles<NewOemAssetStyles>;
  onSaved: (assetId: string) => void;
};

export const NewOemAsset = (props: NewOemAssetProps) => {
  const TopComponentBar = props.topComponentBar;
  const params = useLocalSearchParams();
  const { tagId, tagCode } = params;
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace();
  const { getLocation } = useCurrentLocation();
  const products = useAllProducts(workspace?.id);
  const formSchema = yupObject({
    product: string().required(),
    name: string().required(),
    grippId: string().required(),
  });
  const { mutate, isPending: isLoading } = useCustomMutation({
    query: createAsset,
  });
  type NewOemAssetFormValues = {
    product: string;
    name: string;
    serialNumber?: string;
    addons?: string[];
    grippId: string;
  };

  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { isValid, isDirty },
  } = useForm<NewOemAssetFormValues>({
    resolver: yupResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      product: '',
      name: '',
      serialNumber: '',
      addons: [],
      grippId: tagCode ? String(tagCode) : '',
    },
  });

  const selectedProductId = watch('product');
  const { product: selectedProduct } = useProduct(selectedProductId || '');
  const previousProductIdRef = useRef<string>('');

  // Clear selected addons when product changes
  useEffect(() => {
    if (
      previousProductIdRef.current &&
      previousProductIdRef.current !== selectedProductId
    ) {
      setValue('addons', [], { shouldDirty: true });
    }
    previousProductIdRef.current = selectedProductId || '';
  }, [selectedProductId, setValue]);

  const canSave = useMemo(() => {
    return isDirty && isValid;
  }, [isValid, isDirty]);

  const productItems = useMemo(() => {
    return (
      products?.products?.map((p: any) => ({
        label: p.name,
        value: p.id,
      })) || []
    );
  }, [products?.products]);

  const tickIconComponent = useMemo(
    () => () => <RadioButton value="" status="checked" />,
    []
  );

  const dropDownPickerProps = useMemo(
    () => ({
      listMode: 'SCROLLVIEW' as const,
      showTickIcon: true,
      selectedItemLabelStyle: { color: Colors.blackText },
      selectedItemContainerStyle: {
        backgroundColor: Colors.white,
      },
      TickIconComponent: tickIconComponent,
      dropDownDirection: 'AUTO' as const,
    }),
    [tickIconComponent]
  );

  const onSubmit = async (data: any) => {
    let location;
    if (tagId) {
      location = await getLocation();
    }
    delete data.grippId;
    const input: any = {
      ...data,
      ...(tagId && { tagId }),
      ...(location && { lastLocation: location }),
      workspaceId: workspace.id,
    };

    mutate(
      { input: input },
      {
        onSettled: (response: any) => {
          const assetId = response?.asset?.addAsset?.id;
          if (assetId) props.onSaved(assetId);
        },
      }
    );
  };

  const cancelButton = (
    <LeftButton
      label={t('topNav.cancel')}
      hideCaret={true}
      onPress={() => props.onCancel()}
      style={[styles.baseButton, styles.cancelButton]}
    />
  );
  const addButton = (
    <SaveButton
      onPress={handleSubmit(onSubmit)}
      label={t('topNav.add')}
      disabled={!canSave}
      activeTextColor={Colors.white}
      style={[
        styles.baseButton,
        canSave ? styles.addButtonEnabled : styles.addButtonDisabled,
      ]}
    />
  );

  return (
    <>
      <TopComponentBar
        title={t('asset.newTitle')}
        left={cancelButton}
        right={addButton}
      />
      <KeyboardSafeView style={[props.styles.container]}>
        <ScrollView
          showsHorizontalScrollIndicator={false}
          scrollEnabled
          showsVerticalScrollIndicator={false}
          style={props.styles.contentContainer}
        >
          <View>
            <View style={styles.baseContainer}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {`1. ${t('asset.productType')}`}
                </Text>
              </View>
              <Select
                label=""
                listMode={Platform.OS === 'web' ? 'SCROLLVIEW' : 'MODAL'}
                items={productItems}
                onSelectItem={(item: any) => {
                  setValue('product', item);
                }}
                dropDownPickerProps={dropDownPickerProps}
                dropDownContainerStyle={
                  Platform.OS === 'android'
                    ? {
                        position: 'relative', // fix scroll issue for Android
                        top: 0, // fix gap on Android
                      }
                    : undefined
                }
              />
            </View>
            <View style={[styles.baseContainer, styles.formItem]}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {`2. ${t('asset.name')}:`}
                </Text>
              </View>
              <ControlledTextInput
                testID="add-asset-name"
                control={control as any}
                name="name"
                outlinedTextInput
                disableInnerStyle
              />
            </View>
            <View style={[styles.baseContainer, styles.formItem]}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {`3. ${t('asset.serialNumber')}`}
                </Text>
              </View>
              <ControlledTextInput
                testID="add-asset-serial-number"
                control={control as any}
                name="serialNumber"
                outlinedTextInput
                disableInnerStyle
                placeholder={t('common.optional')}
              />
            </View>
            <View style={[styles.baseContainer, styles.formItem]}>
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {`4. ${t('asset.addons', {
                    count: selectedProduct?.addons?.length || 0,
                  })}`}
                </Text>
              </View>
              <ControlledCheckboxItems
                control={control as any}
                name="addons"
                options={
                  selectedProduct?.addons
                    ?.slice()
                    .sort((a: any, b: any) => a.name.localeCompare(b.name))
                    .map((p: any) => ({
                      label: p.name,
                      value: p.id,
                    })) || []
                }
                position="leading"
                mode="android"
                labelStyle={{ textAlign: 'left' }}
              />
            </View>
            <View
              style={[
                styles.baseContainer,
                styles.formItem,
                styles.formItemBottom,
              ]}
            >
              <View>
                <Text variant={'titleMedium'} style={styles.formItemTitle}>
                  {`5. ${t('asset.tagCode')}`}
                </Text>
              </View>
              <ControlledTextInput
                testID="add-asset-gripp-id"
                control={control as any}
                name="grippId"
                outlinedTextInput
                editable={false}
                disableInnerStyle={true}
                textColor="#8E8E93"
                backgroundColor="#E9E9EF"
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardSafeView>
    </>
  );
};

const createAsset = `
  mutation Asset($input: AddAssetInput!) {
    asset {
      addAsset(input: $input) {
        id
      }
    }
  }
`;

const styles = StyleSheet.create({
  baseContainer: {
    backgroundColor: Colors.white,
    borderRadius: 8,
    paddingVertical: Spacing.basePadding.paddingVertical,
    paddingHorizontal: Spacing.basePadding.base,
    flexDirection: 'column',
    width: '100%',
  },
  baseButton: {
    width: 64,
    height: 36,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    borderRadius: 10,
    marginHorizontal: Spacing.baseMargin.base,
  },
  cancelButton: {
    marginLeft: 8,
    marginRight: Spacing.baseMargin.marginHorizontal * 1.3,
  },
  addButtonDisabled: {
    backgroundColor: Colors.grayDivider,
  },
  addButtonEnabled: {
    backgroundColor: Colors.primary,
  },
  container: {
    ...Layout.container,
    backgroundColor: Colors.primaryGrayBackground,
  },
  contentContainer: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
  },
  formItem: {
    width: '100%',
    marginTop: Spacing.baseMargin.marginVertical,
  },
  formItemBottom: {
    marginBottom: 40,
  },
  formItemTitle: {
    fontSize: 16,
    lineHeight: 16,
    color: Colors.black,
    fontWeight: '700',
    marginBottom: Spacing.baseMargin.marginVertical,
  },
});
